<template>
  <div class="pwt--configuration-panel">
    <ul class="highlighter">
      <li
        v-for="item of items"
        :class="{ active: currentItem === item.value }"
        :key="item.value"
        @mouseenter="currentItem = item.value"
        @focus="currentItem = item.value"
        @click=";(currentItem = item.value), (hover = true)"
      >
        <SloIcon :name="getComponentIconName(item.value)" />
        <div>{{ item.label }}</div>
      </li>
    </ul>
    <div class="container">
      <div class="graphic">
        <div class="overlay">
          <SloIcon
            name="CircleSolid"
            v-for="item of items"
            :key="item.value"
            @mouseover.native=";(currentItem = item.value), (hover = true)"
            @focus="currentItem = item.value"
            @click="currentItem = item.value"
            :style="
              mobile
                ? { display: 'none' }
                : {left: `${(item.x / width) * 100}%`, top: `${(item.y / height) * 100}%`}
            "
            :class="
              mobile
                ? { active: currentItem === item.value }
                : { active: currentItem === item.value && hover == true }
            "
          />
          <ConfigurationCard
            v-if="mobile ? selectedItem : selectedItem && hover"
            @mouseover.native="hovering"
            @mouseleave.native="notHovering"
            :style="cardCoords"
          >
            <template v-slot:image>
              <img :src="`/themes/sloan/img/pwt/cards/${selectedItem.value}.jpg?v=20200622`" width="200" height="200" />
            </template>
            <template v-slot:title>
              {{ selectedItem.title || selectedItem.label }}
            </template>
            <p v-html="selectedItem.content" />
            <template v-slot:link>
              <a :href="`${selectedItem.link}`" v-html="selectedItem.linkText"></a>
            </template>
          </ConfigurationCard>
        </div>
        <div
          class="base"
          :style="{
            display: mobile ? 'none' : 'block',
            paddingBottom: (100 / (width / height)).toFixed(2) + '%',
          }"
        >
          <slot name="base" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConfigurationCard from './ConfigurationCard.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';

export default {
  name: 'Configuration',
  components: {
    ConfigurationCard,
    SloIcon
  },
  data: () => ({
    currentItem: null,
    innerWidth: 0,
    hover: false
  }),
  created() {
    this.innerWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    this.currentItem = this.items[0].value;
  },
  computed: {
    mobile() {
      return this.innerWidth < 1080;
    },
    selectedItem() {
      return this.items.find(i => i.value === this.currentItem);
    },
    cardCoords() {
      if (!this.selectedItem) return {};
      const {x, y} = this.selectedItem;
      const collisionX = (x / this.width) * window.innerWidth + 540 > window.innerWidth;
      const xPercent = (x / this.width) * 100 + 3.5;
      const collisionY = (y / this.height) * window.innerHeight + 400 > window.innerHeight;
      const yPercent = (y / this.height) * 55;
      return {
        [!collisionX ? 'left' : 'right']: `${!collisionX ? xPercent : 108.5 - xPercent}%`,
        [!collisionY ? 'top' : 'bottom']: `${!collisionY ? yPercent : yPercent}%`
      };
    }
  },
  props: {
    width: Number,
    height: Number,
    items: {type: Array}
  },
  methods: {
    handleResize() {
      this.innerWidth = window.innerWidth;
    },
    getComponentIconName(value) {
      switch (value) {
        case 'button':
          return 'PWTButton';
        case 'controller':
          return 'PWTController';
        case 'flushometer':
          return 'PWTFlushometer';
        case 'manifold':
          return 'PWTManifold';
        case 'showerhead':
          return 'PWTShowerhead';
        case 'software':
          return 'PWTSoftware';
        case 'solenoid':
          return 'PWTSolenoid';
        case 'transformer':
          return 'PWTTransformer';
        default:
          return null;
      }
    },
    hovering() {
      this.hover = true;
    },
    notHovering() {
      this.hover = false;
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.pwt--configuration-panel {
  display: flex;
  flex-direction: column;
  background-color: #f7fafc;
  padding-top: 16px;

  @media (min-width: 1080px) {
    padding-top: 0;
  }

  .highlighter {
    order: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    margin: 0;
    margin-top: 32px;
    padding: 0 64px;

    li {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 16px;
      list-style: none;
      white-space: nowrap;

      > * {
        pointer-events: none;
      }

      .slo-icon {
        font-size: 32px;
        color: $color-gray-400;
        transition: color 300ms cubic-bezier(0.23, 1, 0.32, 1);
      }

      div {
        @include text('lg');
        margin-top: 8px;
        color: $color-gray-700;
        transition: color 300ms cubic-bezier(0.23, 1, 0.32, 1);
      }

      &.active,
      &:hover,
      &:active,
      &:focus {
        .slo-icon {
          color: $color-blue-400;
        }

        div {
          color: $color-gray-900;
        }
      }
    }

    @media (min-width: 1080px) {
      display: none;
    }
  }

  .graphic {
    position: relative;
    max-width: 1080px;
    margin: 0 auto;

    @media (min-width: 1080px) {
      min-height: 650px;
    }

    .base {
      width: 100%;
      height: 100%;

      > svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }

  .overlay {
    display: flex;
    align-items: center;
    justify-content: center;

    & > .ratio {
      display: block;
    }

    .slo-icon {
      position: relative;
      top: 0;
      left: 0;
      font-size: 32px;
      width: 32px !important;
      height: 32px !important;
      color: $color-blue-400;
      background-color: rgba($color-gray-400, 0.7);
      border: 1px solid;
      border-color: transparent;
      border-radius: 50px;
      transition: 950ms cubic-bezier(0.23, 1, 0.32, 1);
      transition-property: background-color, transform, border-color;
      box-shadow: 0 0 0 0 rgba($color-gray-400, 1);
      animation: pulse 2s infinite;

      svg {
        width: 12px;
        height: 12px;
      }

      &::before {
        position: absolute;
        top: 50%;
        left: 50%;
        content: '';
        z-index: -1;
        transform: translate(-50%, -50%) scale(0);
        transition: 850ms cubic-bezier(0.23, 1, 0.32, 1);
        transition-property: opacity, transform;
      }

      &.active {
        z-index: 1;
        transform: scale(0.9);
        animation: unset;
        background-color: rgba($color-gray-400, 0.7);
        border-color: $color-gray-400;

        &::before {
          z-index: 10;
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
          padding: 24px;
        }

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 25px !important;
          height: 25px !important;
          z-index: -1;
        }
      }

      @media screen and (min-width: 1080px) {
        .configuration-card {
          opacity: 0;
          transition: 300ms ease-out;
          transform: translate(-50%, -50%) scale(0);
          transition-property: opacity, transform;
          pointer-events: none;
        }

        &:hover,
        &:active,
        &:focus {
          & .configuration-card {
            z-index: 10;
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
            pointer-events: initial;
          }
        }
      }
    }
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba($color-gray-500, 0.8);
    }

    70% {
      box-shadow: 0 0 0 5px rgba($color-gray-500, 0);
    }

    100% {
      box-shadow: 0 0 0 0 rgba($color-gray-500, 0);
    }
  }

  @media screen and (min-width: 1080px) {
    .highlighter {
      order: unset;
      margin-top: 0;
      margin-bottom: 32px;

      &::after {
        content: none;
      }
    }
    .configuration-card {
      position: absolute;
      z-index: 0;
      margin-right: -40px;
    }

    .overlay {
      position: absolute;
      z-index: 1;
      width: 100%;
      height: 100%;

      .slo-icon {
        position: absolute;
      }
    }
  }
}
</style>
